import { EyeIcon } from "@heroicons/react/24/outline";
import { useApplicationModals } from "app/common/hooks";
import { Badge } from "app/components/atoms/badge";
import {
  IconButton,
  ServerSidePaginationButtons,
} from "app/components/atoms/button";
import { SearchInput } from "app/components/atoms/input";
import { FormHeader } from "app/components/atoms/layout";
import { Select } from "app/components/atoms/select";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import api from "app/integration/api";
import {
  useCollector,
  usePaginatedCollector,
} from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import AppModals from "app/modals";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

function ProductCodePage() {
  const searchTimer = useRef(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchInputRef, setSearchInputRef] = useState("");
  let { id } = useParams();
  const [productVariant] = useCollector({
    modelName: "productVariant",
    params: { id: id },
  });

  const breadcrumbItems = [
    { label: "Product", href: "/product" },
    {
      label: "Product Variant",
      href: `/product/detail/${productVariant[0]?.product_id}`,
    },
    { label: "Product Code", href: "#" },
  ];

  const { openModal, setPayloads } = useApplicationModals();

  const [params, setParams] = useState({
    ref: "",
    qr_code: "",
    status: null,
    product_variant_id: id,
    page: 1,
    limit: 20,
  });

  const [salesChannel, setSalesChannel] = useCollector({
    modelName: "salesChannel",
  });

  const [salesOrder, setSalesOrder] = useCollector({
    modelName: "salesOrder",
  });

  const salesChannelById = (sales_channel_id) => {
    return salesChannel.find((item) => item.id === sales_channel_id);
  };
  const salesOrderById = (sales_order_id) => {
    return salesOrder.find((item) => item.id === sales_order_id);
  };

  useEffect(() => {
    collect({
      ...params,
    });
  }, [params]);

  const [
    productCodes,
    setProductCodes,
    productCodesLoader,
    collect,
    productCodesPaginationProps,
  ] = usePaginatedCollector({ modelName: "productCode", params: params });

  return (
    <MainLayout
      activeSidebarNavigation="product"
      breadcrumbItems={breadcrumbItems}
      pageTitle={
        productVariant[0]?.product_detail?.name +
        " - " +
        productVariant[0]?.product_detail?.code
      }
      headingButtons={[
        {
          label: "Download All QR Code",
          type: "button",
          onClick: async () => {
            try {
              // Fetch all QR codes without pagination
              const response = await api.productCode.index({
                limit: 999999, // Very high limit to get all codes
                product_variant_id: productVariant[0]?.id,
              });

              const codesWithDetails = response.data.map((code) => ({
                ...code,
                product_detail: productVariant[0]?.product_detail,
                size: productVariant[0]?.size,
                color: productVariant[0]?.color,
              }));

              setPayloads("qrCode.view", {
                product: codesWithDetails,
                multiple: true,
              });
              openModal("qrCode.view");
            } catch (err) {
              console.error("Error fetching QR codes:", err);
              setPayloads("errorMessage.view", {
                title: "Error",
                message: "Gagal mengambil QR code",
              });
              openModal("errorMessage.view");
            }
          },
        },
      ]}
    >
      <AppModals
        items={["qrCode.view"]}
        onSuccess={{
          "qrCode.view": () => collect(),
        }}
      />
      <div className="px-2 py-2 border bg-primary-100">
        <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm">
          {/* <div className="flex items-center gap-1">
            <span className="text-sm uppercase text-gray-500">Code:</span>
            <span className="font-medium text-base">
              {productVariant[0]?.product_detail?.code}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-sm uppercase text-gray-500">Product:</span>
            <span className="font-medium text-base">
              {productVariant[0]?.product_detail?.name}
            </span>
          </div> */}
          <div className="flex items-center gap-1">
            <span className="text-sm uppercase text-gray-500">Color:</span>
            <span className="font-medium text-base">
              {productVariant[0]?.color?.name}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-sm uppercase text-gray-500">Size:</span>
            <span className="font-medium text-base">
              {productVariant[0]?.size?.name}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-sm uppercase text-gray-500">Price:</span>
            <span className="font-medium text-base">
              Rp.{" "}
              <Typography.Currency
                number={productVariant[0]?.product_detail?.price}
              />
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-sm uppercase text-gray-500">Weight:</span>
            <span className="font-medium text-base">
              {productVariant[0]?.product_detail?.weight} gram
            </span>
          </div>
        </div>
      </div>
      <Table.Container>
        <Table.UtilitiesWrapper>
          <Table.SearchBarWrapper>
            <FormHeader appearance="none">
              <SearchInput testId="product-code-search-input"
                placeholder="Scan QR Code"
                type="text"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value?.replace(/"/g, "");
                  // Update input value immediately without triggering API call
                  setSearchInput(value);

                  // Clear existing timer
                  if (searchTimer.current) {
                    clearTimeout(searchTimer.current);
                  }

                  // Set new timer for 3 seconds
                  searchTimer.current = setTimeout(() => {
                    setParams((prev) => ({
                      ...prev,
                      qr_code: value.trim(),
                      page: 1,
                    }));
                  }, 3000);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    // Clear any pending timer
                    if (searchTimer.current) {
                      clearTimeout(searchTimer.current);
                    }
                    const value = e.target.value?.replace(/"/g, "");
                    // Trigger search immediately
                    setParams((prev) => ({
                      ...prev,
                      qr_code: value.trim(),
                      page: 1,
                    }));
                  }
                }}
              />
              <SearchInput testId="product-code-search-input"
                placeholder="Ref Penerimaan Barang"
                type="text"
                value={searchInputRef}
                onChange={(e) => {
                  const value = e.target.value?.replace(/"/g, "");
                  // Update input value immediately without triggering API call
                  setSearchInputRef(value);

                  // Clear existing timer
                  if (searchTimer.current) {
                    clearTimeout(searchTimer.current);
                  }

                  // Set new timer for 3 seconds
                  searchTimer.current = setTimeout(() => {
                    setParams((prev) => ({
                      ...prev,
                      ref: value.trim(),
                      page: 1,
                    }));
                  }, 3000);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    // Clear any pending timer
                    if (searchTimer.current) {
                      clearTimeout(searchTimer.current);
                    }
                    const value = e.target.value?.replace(/"/g, "");
                    // Trigger search immediately
                    setParams((prev) => ({
                      ...prev,
                      ref: value.trim(),
                      page: 1,
                    }));
                  }
                }}
              />
              <Select testId="product-code-status-select"
                label="Status"
                placeholder="Status Barang"
                value={params.status}
                onChange={(e) =>
                  setParams({ ...params, status: e.target.value })
                }
                options={[
                  {
                    value: null,
                    label: "Semua Status",
                  },
                  {
                    value: "warehouse",
                    label: "Warehouse",
                  },
                  {
                    value: "packing",
                    label: "Packing",
                  },
                  {
                    value: "delivery",
                    label: "Delivery",
                  },
                  {
                    value: "return",
                    label: "Retur",
                  },
                  {
                    value: "scrap",
                    label: "Scrap",
                  },
                ]}
              />
            </FormHeader>
          </Table.SearchBarWrapper>
          {/* <Table.UtilitiesButtonWrapper>
            <IconButton testId="product-code-printer-button" icon={PrinterIcon} />
          </Table.UtilitiesButtonWrapper> */}
        </Table.UtilitiesWrapper>
        <Table.Wrapper asIndex={false}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Ref</Table.Heading>
              <Table.Heading>Kode QR</Table.Heading>
              <Table.Heading>Status</Table.Heading>
              <Table.Heading>Receive Date</Table.Heading>
              <Table.Heading>Packing Date</Table.Heading>
              <Table.Heading>Delivery Date</Table.Heading>
              <Table.Heading>Retur Date</Table.Heading>
              <Table.Heading>Scrap Date</Table.Heading>
              <Table.Heading>Order Code</Table.Heading>
              <Table.Heading>Sales Channel</Table.Heading>
              <Table.Heading></Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {productCodes?.map((product, key) => (
              <Table.BodyRow key={key}>
                <Table.Cell>{product?.ref}</Table.Cell>
                <Table.Cell>
                  <div className="flex items-center justify-between gap-2">
                    {/* <span>{product.id}</span> */}
                    <span className="tabular-nums text-xs">
                      <pre>{product.qr_code}</pre>
                    </span>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    color={
                      product?.status == "packing"
                        ? "yellow"
                        : product?.status == "delivery"
                          ? "yellow"
                          : product?.status == "retur"
                            ? "red"
                            : product?.status == "scrap"
                              ? "red"
                              : product?.status == "warehouse"
                                ? "gray"
                                : "blue"
                    }
                    size="sm"
                  >
                    {product?.status}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  {product?.createdAt ? (
                    <Typography.Date
                      format="dd/MM/yy hh:mm"
                      size="sm"
                      date={product?.createdAt}
                    />
                  ) : (
                    "-"
                  )}
                </Table.Cell>
                <Table.Cell>
                  {product?.scan_packing_date ? (
                    <Typography.Date
                      format="dd/MM/yy hh:mm"
                      size="sm"
                      date={product?.scan_packing_date}
                    />
                  ) : (
                    "-"
                  )}
                </Table.Cell>
                <Table.Cell>
                  {product?.scan_delivery_date ? (
                    <Typography.Date
                      format="dd/MM/yy hh:mm"
                      size="sm"
                      date={product?.scan_delivery_date}
                    />
                  ) : (
                    "-"
                  )}
                </Table.Cell>
                <Table.Cell>
                  {product?.scan_retur_date ? (
                    <Typography.Date
                      format="dd/MM/yy hh:mm"
                      size="sm"
                      date={product?.scan_retur_date}
                    />
                  ) : (
                    "-"
                  )}
                </Table.Cell>
                <Table.Cell>
                  {product?.scan_scrap_date ? (
                    <Typography.Date
                      format="dd/MM/yy hh:mm"
                      size="sm"
                      date={product?.scan_scrap_date}
                    />
                  ) : (
                    "-"
                  )}
                </Table.Cell>
                <Table.Cell>
                  <p>{salesOrderById(product?.sales_order_id)?.order_code || "-"}</p>
                  {salesOrderById(product?.sales_order_id)?.createdAt && (
                    <Typography.Date
                      format="dd MMM yyyy"
                      size="xs"
                      date={salesOrderById(product?.sales_order_id)?.createdAt}
                    />
                  )}
                </Table.Cell>
                <Table.Cell>
                  {salesChannelById(product?.sales_channel_id)?.name || "-"}
                </Table.Cell>
                <Table.Cell>
                  <div className="flex gap-1 items-center">
                    <IconButton testId="product-code-eye-button"
                      type="IconButton"
                      icon={EyeIcon}
                      onClick={() => {
                        const codeWithDetails = {
                          ...product,
                          product_detail: productVariant[0]?.product_detail,
                          size: productVariant[0]?.size,
                          color: productVariant[0]?.color,
                        };
                        setPayloads("qrCode.view", {
                          product: codeWithDetails,
                          multiple: false,
                        });
                        openModal("qrCode.view");
                      }}
                    >
                      Preview
                    </IconButton>
                  </div>
                </Table.Cell>
              </Table.BodyRow>
            ))}
          </Table.Body>
        </Table.Wrapper>
        <Table.PaginationWrapper>
          <ServerSidePaginationButtons
            paginationProps={productCodesPaginationProps}
            params={params}
            setParams={setParams}
          />
        </Table.PaginationWrapper>
      </Table.Container>
    </MainLayout>
  );
}

export default ProductCodePage;
